import {
  PortCallPurpose,
  PortCallReasonCategory,
  PortCallReasonItem,
} from '@navstation-portal/reporting/data-model/vessel-report';

// Category/subgroup grouping for Port Call Reasons - the hardcoded mapping" per PBI 134260,
// bridging until the API itself returns categories. Any value the API returns that isn't listed below
// still renders - it falls back into the "Other Operational Reasons" subgroup rather than being
// hidden, per the PBI's explicit requirement.

interface SubgroupDefinition {
  label: string;
  infoHint?: string;
  reasonLabels: Record<string, string>;
}

interface CategoryDefinition {
  label: string;
  infoHint?: string;
  subgroups: SubgroupDefinition[];
}

const FALLBACK_SUBGROUP: SubgroupDefinition = {
  label: 'Other Operational Reasons',
  reasonLabels: {
    MeansOfTransportCustomsClearance: 'Customs Clearance',
    QuarantineInspection: 'Quarantine Inspection',
    UnderGovernmentOrder: 'Under Government Order',
    GoodWillVisit: 'Good Will Visit',
    AwaitingOrders: 'Awaiting Orders',
    LaidUp: 'Laid Up',
    Refuge: 'Refuge',
    PassThrough: 'Pass Through',
    Miscellaneous: 'Miscellaneous',
  },
};

const PORT_CALL_REASON_CATEGORIES: CategoryDefinition[] = [
  {
    label: 'Commercial Operations',
    infoHint: 'Triggers Port of Call',
    subgroups: [
      {
        label: 'Cargo',
        reasonLabels: {
          CargoOperations: 'Cargo Operations',
          LoadingCargo: 'Loading Cargo',
          UnloadingCargo: 'Unloading Cargo',
        },
      },
      {
        label: 'Passenger',
        reasonLabels: {
          PassengerMovement: 'Passenger Movement',
          CruiseLeisureRecreation: 'Cruise Leisure Recreation',
        },
      },
      {
        label: 'Offshore Operations',
        infoHint: 'Applicable to Offshore Vessels',
        reasonLabels: {
          OffshoreMobilizationOperations: 'Offshore Mobilization Operations',
          CrewMovement: 'Crew Movement',
          ChangingCrew: 'Changing Crew',
          TransferOfPersonnel: 'Transfer Of Personnel',
        },
      },
    ],
  },
  {
    label: 'Technical & Operational Stops',
    subgroups: [
      {
        label: 'Vessel Services',
        reasonLabels: {
          TakingBunkers: 'Taking Bunkers',
          TakingSupplies: 'Taking Supplies',
          WasteDisposal: 'Waste Disposal',
        },
      },
      {
        label: 'Maintenance & Yard',
        reasonLabels: {
          Repair: 'Repair',
          RepairInDryDock: 'Repair in Dry Dock',
          RepairInWetDock: 'Repair in Wet Dock',
          CargoTankCleaning: 'Cargo Tank Cleaning',
          DeGassing: 'De-Gassing',
        },
      },
      FALLBACK_SUBGROUP,
    ],
  },
];

const ALL_SUBGROUPS = PORT_CALL_REASON_CATEGORIES.flatMap((category) => category.subgroups);

const findSubgroup = (value: string): SubgroupDefinition =>
  ALL_SUBGROUPS.find((s) => value in s.reasonLabels) ?? FALLBACK_SUBGROUP;

const toReason = (purpose: PortCallPurpose): { subgroup: SubgroupDefinition; item: PortCallReasonItem } => {
  const subgroup = findSubgroup(purpose.value);
  const label = subgroup.reasonLabels[purpose.value] ?? purpose.value;
  return { subgroup, item: { value: purpose.value, label, description: purpose.description } };
};

export function groupPortCallPurposes(purposes: PortCallPurpose[] | undefined): PortCallReasonCategory[] {
  const reasons = (purposes ?? []).map(toReason);

  return PORT_CALL_REASON_CATEGORIES.map((category) => ({
    label: category.label,
    infoHint: category.infoHint,
    subgroups: category.subgroups
      .map((subgroup) => ({
        label: subgroup.label,
        infoHint: subgroup.infoHint,
        reasons: reasons
          .filter((r) => r.subgroup === subgroup)
          .map((r) => r.item)
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter((subgroup) => subgroup.reasons.length > 0),
  })).filter((category) => category.subgroups.length > 0);
}
